import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {afterNextRender, Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {PrimeNGConfig} from 'primeng/api';
import {Capacitor} from '@capacitor/core';
import {SafeArea} from '@capacitor-community/safe-area';
import {
  AppearanceSettingsInterface, appearanceScale, DEFAULT_APPEARANCE, normalizeAppearance,
} from '../../api/interfaces/appearance-settings.interface';
import {CurrentUserViewModel} from '../models/current-user.view-model';
import {StorageService} from './storage.service';
import {TokenDataProviderService} from './token-data-provider.service';
import {UserSettingsWriterService} from './user-settings-writer.service';
import {AppearanceStylesheetService} from './appearance-stylesheet.service';

export type AppearanceFeedback = 'idle' | 'saving' | 'saved' | 'save_error' | 'load_error';

@Injectable({providedIn: 'root'})
export class AppearanceService implements OnDestroy {
  private readonly state = new BehaviorSubject<AppearanceSettingsInterface>({...DEFAULT_APPEARANCE});
  public readonly settings$ = this.state.asObservable();
  public feedback: AppearanceFeedback = 'idle';
  public applying = false;
  public ready = false;
  private accountId = 0;
  private hydrated = false;
  private session = 0;
  private revision = 0;
  private dirty = false;
  private saving = false;
  private confirmed = {...DEFAULT_APPEARANCE};
  private desired = {...DEFAULT_APPEARANCE};
  private failed?: AppearanceSettingsInterface;
  private serverKey = '';
  private saveTimer?: ReturnType<typeof setTimeout>;
  private readonly subscriptions = new Subscription();
  private readonly resize = () => this.applyScale();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly storage: StorageService,
    private readonly tokens: TokenDataProviderService,
    private readonly writer: UserSettingsWriterService,
    private readonly stylesheets: AppearanceStylesheetService,
    private readonly prime: PrimeNGConfig,
    private readonly zone: NgZone,
  ) {
    // Hydration must finish before browser-only cache and DOM changes are applied.
    afterNextRender(() => this.zone.run(() => this.initialize()));
  }

  public get settings(): AppearanceSettingsInterface { return this.state.value; }
  public get canEdit(): boolean { return this.ready && this.hydrated && !!this.accountId; }
  public get effectiveMenuTheme(): string {
    return this.settings.layoutMode === 'dark' ? 'dark' : this.settings.menuTheme;
  }
  public get scale(): number {
    return appearanceScale(this.settings, this.document.defaultView?.innerWidth ?? 1200);
  }
  public get logo(): string {
    return this.logoFor(this.settings.topbarTheme);
  }
  public get mobileLogo(): string { return this.logo; }

  public initialize(): void {
    if (this.ready || !isPlatformBrowser(this.platformId)) { return; }
    this.ready = true;
    void this.display(this.desired);
    this.document.defaultView?.addEventListener('resize', this.resize, {passive: true});
    this.subscriptions.add(this.tokens.user$.subscribe(user => this.receiveUser(user)));
  }

  public change(patch: Partial<AppearanceSettingsInterface>): void {
    if (!this.canEdit) { return; }
    this.failed = undefined;
    this.feedback = this.saving ? 'saving' : 'idle';
    clearTimeout(this.saveTimer);
    this.desired = normalizeAppearance({...this.desired, ...patch});
    this.dirty = true;
    void this.display(this.desired).then(applied => {
      if (applied) { this.scheduleSave(); }
    });
  }

  public reset(): void { this.change({...DEFAULT_APPEARANCE}); }
  public retry(): void { if (this.failed) { this.change(this.failed); } }

  private receiveUser(user: CurrentUserViewModel): void {
    const id = user?.id || 0;
    if (id !== this.accountId) {
      this.session++;
      this.accountId = id;
      this.hydrated = false;
      this.serverKey = '';
      this.dirty = this.saving = false;
      this.failed = undefined;
      this.feedback = 'idle';
      clearTimeout(this.saveTimer);
      this.confirmed = {...DEFAULT_APPEARANCE};
      this.desired = this.readCache(id);
      void this.display(this.desired);
    }
    // JWT-only user emissions are not authoritative settings responses.
    if (!id || !user['@id'] || !user.settings) { return; }
    this.hydrated = true;
    const remote = normalizeAppearance(user.settings.appearance);
    const key = JSON.stringify(remote);
    if (key === this.serverKey) { return; }
    this.serverKey = key;
    if (this.saving || this.dirty) { return; }
    this.confirmed = remote;
    this.desired = remote;
    this.cache(remote);
    void this.display(remote);
  }

  private async display(settings: AppearanceSettingsInterface): Promise<boolean> {
    const revision = ++this.revision;
    this.applying = true;
    try {
      const applied = await this.stylesheets.load(settings);
      if (!applied || revision !== this.revision) { return false; }
      this.state.next({...settings});
      this.applyDocument();
      return true;
    } catch {
      if (revision === this.revision) {
        this.failed = {...settings};
        this.desired = {...this.settings};
        this.dirty = false;
        this.feedback = 'load_error';
      }
      return false;
    } finally {
      if (revision === this.revision) { this.applying = false; }
    }
  }

  private scheduleSave(): void {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.save(), 400);
  }

  private save(): void {
    if (!this.dirty || this.saving || this.applying || !this.canEdit) { return; }
    const session = this.session;
    const snapshot = {...this.settings};
    this.dirty = false;
    this.saving = true;
    this.feedback = 'saving';
    // The shared writer owns the HTTP lifetime, independently of the open panel.
    this.subscriptions.add(this.writer.update(settings => ({...settings, appearance: snapshot})).subscribe({
      next: response => {
        if (session !== this.session) { return; }
        this.saving = false;
        this.confirmed = normalizeAppearance(response.settings?.appearance);
        this.cache(this.confirmed);
        if (this.feedback !== 'load_error') { this.feedback = this.dirty ? 'idle' : 'saved'; }
        if (this.dirty && !this.applying) { this.scheduleSave(); }
      },
      error: () => {
        if (session !== this.session) { return; }
        this.saving = false;
        this.failed = {...this.desired};
        this.dirty = false;
        clearTimeout(this.saveTimer);
        this.desired = {...this.confirmed};
        this.feedback = 'save_error';
        void this.display(this.confirmed);
      },
    }));
  }

  private cache(settings: AppearanceSettingsInterface): void {
    if (!this.accountId) { return; }
    try {
      this.storage.setItem(this.cacheKey(this.accountId), JSON.stringify({version: 1, accountId: this.accountId, settings}));
    } catch { /* Storage may be blocked or full; the API remains authoritative. */ }
  }

  private readCache(accountId: number): AppearanceSettingsInterface {
    if (accountId) {
      try {
        const cached = JSON.parse(this.storage.getItem(this.cacheKey(accountId)) || 'null');
        if (cached?.version === 1 && cached.accountId === accountId) { return normalizeAppearance(cached.settings); }
      } catch { /* Corrupt or unavailable storage falls back to defaults. */ }
    }
    return {...DEFAULT_APPEARANCE};
  }

  private cacheKey(accountId: number): string { return `wtg.appearance.v1.${accountId}`; }

  private applyScale(): void {
    this.document.documentElement.style.fontSize = `${this.scale}px`;
  }

  private applyDocument(): void {
    const root = this.document.documentElement;
    root.setAttribute('data-wtg-appearance', this.settings.layoutMode);
    root.style.colorScheme = this.settings.layoutMode;
    root.dir = this.settings.isRTL ? 'rtl' : 'ltr';
    root.classList.toggle('p-input-filled', this.settings.inputStyle === 'filled');
    root.classList.toggle('p-ripple-disabled', !this.settings.ripple);
    this.prime.ripple = this.settings.ripple;
    this.applyScale();
    const topbar = this.settings.topbarTheme;
    const mode = this.settings.layoutMode;
    const background = this.topbarBackground(topbar);
    root.style.setProperty('--wtg-topbar-bg', background);
    root.style.setProperty('--wtg-topbar-text', this.isLightTopbar(topbar) ? '#616161' : '#ffffff');
    this.document.querySelector('meta[name="theme-color"]')?.setAttribute('content', background);
    if (Capacitor.isNativePlatform()) {
      // Keep native bar changes ordered even if a previous plugin call is pending.
      this.nativeBars = this.nativeBars.then(() => SafeArea.enable({config: {
        customColorsForSystemBars: true,
        statusBarColor: '#00000000', statusBarContent: this.isLightTopbar(topbar) ? 'dark' : 'light',
        navigationBarColor: '#00000000', navigationBarContent: mode === 'light' ? 'dark' : 'light',
      }})).then(() => undefined).catch(() => undefined);
    }
  }

  private nativeBars: Promise<void> = Promise.resolve();
  private isLightTopbar(theme: string): boolean { return ['white', 'yellow', 'amber', 'orange', 'lime'].includes(theme); }
  private logoFor(theme: string): string {
    return `assets/layout/images/wtg-logo-transparent-${this.isLightTopbar(theme) ? 'black' : 'white'}-sm.webp`;
  }
  private topbarBackground(theme: string): string {
    // Match the layout stylesheet rather than the palette's preview swatches.
    return ({dark: '#1e1e1e', white: '#ffffff', blue: '#1565C0', lightblue: '#0288D1',
      deeppurple: '#4527A0', purple: '#6A1B9A', pink: '#AD1457', cyan: '#0097A7', teal: '#00796B',
      green: '#43A047', lightgreen: '#689F38', lime: '#AFB42B', yellow: '#FBC02D', amber: '#FFA000',
      orange: '#FB8C00', deeporange: '#D84315', brown: '#5D4037', grey: '#616161', bluegrey: '#546E7A',
      indigo: '#3F51B5'} as Record<string, string>)[theme];
  }

  public ngOnDestroy(): void {
    clearTimeout(this.saveTimer);
    this.revision++;
    this.stylesheets.cancel();
    this.subscriptions.unsubscribe();
    this.document.defaultView?.removeEventListener('resize', this.resize);
  }
}
